import { useState, useEffect, type ReactNode } from 'react';
import {
  Plus,
  Edit2,
  Trash2,
  GripVertical,
  Check,
  X,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Spinner, ConfirmDialog } from '@centraoneplus/ui';
import {
  CARD,
  CARD_CLASS,
  CardFoot,
  ColumnHeader,
  EmptyState,
  IconButton,
  Notice,
  StatusChip,
  Button as KitButton,
} from '@shared/ui/kit';
import { SectionHead, FieldLabel } from '../chrome';
import { pipelineService } from '@/services/pipeline.service';
import { fieldDefinitionsService } from '@/services/field-definitions.service';
import type { Pipeline, Stage, StageExitConditions } from '@/types/pipeline';
import { ThemedSelect } from '@components/common/ThemedSelect';

// Color options for stages
const STAGE_COLORS = [
  { name: 'Blue', value: '#3B82F6' },
  { name: 'Green', value: '#10B981' },
  { name: 'Yellow', value: '#F59E0B' },
  { name: 'Red', value: '#EF4444' },
  { name: 'Purple', value: '#8B5CF6' },
  { name: 'Pink', value: '#EC4899' },
  { name: 'Indigo', value: '#6366F1' },
  { name: 'Gray', value: '#6B7280' },
];

// All available ticket statuses — each stage maps to exactly one
const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: 'new', label: 'New' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'pending', label: 'Pending' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
];

// Slugs of the five seeded default stages. These are created automatically with
// every pipeline and cannot be deleted (the delete action is hidden for them).
const SYSTEM_STAGE_SLUGS = ['new', 'in-progress', 'pending', 'resolved', 'closed'];
const isSystemStage = (stage: Stage) => !!stage.slug && SYSTEM_STAGE_SLUGS.includes(stage.slug);

// Lightweight shape of a field definition for the required-fields picker.
interface FieldOption {
  key: string;
  name: string;
}

// Exit-condition toggles surfaced in the stage form. Keys map to the boolean
// flags on StageExitConditions; the backend enforces them when leaving a stage.
const EXIT_CONDITION_OPTIONS: FieldOption[] = [
  { key: 'requiresAssignee', name: 'Must be assigned' },
  { key: 'requiresResolution', name: 'Must be resolved' },
];

interface StageFormData {
  name: string;
  color: string;
  description: string;
  mappedStatus: string;
  wipLimit: number | null;
  requiredFields: string[];
  exitConditions: StageExitConditions;
}

const defaultStageForm: StageFormData = {
  name: '',
  color: '#3B82F6',
  description: '',
  mappedStatus: 'in_progress',
  wipLimit: null,
  requiredFields: [],
  exitConditions: {},
};

interface StagesTabProps {
  pipeline: Pipeline;
  onUpdate: (updatedPipeline: Pipeline) => void;
}

// Render-prop wrapper that makes a row sortable and hands the caller the
// drag-handle props to spread onto the grip icon.
function SortableStageItem({
  id,
  children,
}: {
  id: string;
  // dnd-kit's attribute/listener maps aren't assignable to Record<string, unknown>
  // (no index signature), so they're typed loosely here for the spread.
  children: (handle: {
    attributes: any;
    listeners: any;
    isDragging: boolean;
  }) => ReactNode;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 10 : undefined,
    position: 'relative',
  };
  return (
    <div ref={setNodeRef} style={style}>
      {children({ attributes, listeners, isDragging })}
    </div>
  );
}

export default function StagesTab({ pipeline, onUpdate }: StagesTabProps) {
  const [isCreating, setIsCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [stageForm, setStageForm] = useState<StageFormData>(defaultStageForm);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Stage pending deletion — drives the ConfirmDialog (null = closed).
  const [stageToDelete, setStageToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  // Org-level field definitions — the single source for required-field choices.
  const [fieldDefinitions, setFieldDefinitions] = useState<FieldOption[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const defs = await fieldDefinitionsService.getFieldDefinitions({ isActive: true });
        if (!cancelled) setFieldDefinitions(defs.map((d) => ({ key: d.key, name: d.name })));
      } catch (err) {
        console.error('Error loading field definitions:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const sensors = useSensors(
    // Require a small drag distance so a plain click still toggles/expands.
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const current = pipeline.stages || [];
    const oldIndex = current.findIndex((s) => s.id === active.id);
    const newIndex = current.findIndex((s) => s.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(current, oldIndex, newIndex).map((s, i) => ({
      ...s,
      sortOrder: i,
    }));

    // Optimistically apply the new order, then persist.
    onUpdate({ ...pipeline, stages: reordered });
    try {
      setError(null);
      await pipelineService.reorderStages(
        pipeline.id,
        reordered.map((s) => s.id),
      );
    } catch (err) {
      console.error('Error reordering stages:', err);
      setError('Failed to reorder stages');
      onUpdate({ ...pipeline, stages: current }); // revert
    }
  };

  const handleCreate = async () => {
    if (!stageForm.name.trim()) return;

    try {
      setIsSaving(true);
      setError(null);
      const order = pipeline.stages?.length || 0;

      const newStage = await pipelineService.createStage(pipeline.id, {
        name: stageForm.name,
        color: stageForm.color,
        description: stageForm.description || undefined,
        order,
        mappedStatus: stageForm.mappedStatus,
        wipLimit: stageForm.wipLimit || undefined,
        requiredFields: stageForm.requiredFields,
        exitConditions: stageForm.exitConditions,
      });

      // Update stage with additional properties via updateStage
      // For now we add it to local state
      const stages = [...(pipeline.stages || []), newStage];
      onUpdate({ ...pipeline, stages });

      setIsCreating(false);
      setStageForm(defaultStageForm);
    } catch (err) {
      console.error('Error creating stage:', err);
      setError('Failed to create stage');
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdate = async (stageId: string) => {
    if (!stageForm.name.trim()) return;

    try {
      setIsSaving(true);
      setError(null);

      const updatedStage = await pipelineService.updateStage(stageId, {
        name: stageForm.name,
        color: stageForm.color,
        description: stageForm.description || undefined,
        mappedStatus: stageForm.mappedStatus,
        // Send the number when enabled, or null to CLEAR an existing limit when
        // disabled. Sending undefined would leave the old value (backend only
        // updates wipLimit when it's !== undefined).
        wipLimit: stageForm.wipLimit ?? null,
        requiredFields: stageForm.requiredFields,
        exitConditions: stageForm.exitConditions,
      });

      const stages = pipeline.stages?.map((s) =>
        s.id === stageId ? { ...s, ...updatedStage } : s
      ) || [];
      onUpdate({ ...pipeline, stages });

      setEditingId(null);
      setStageForm(defaultStageForm);
    } catch (err) {
      console.error('Error updating stage:', err);
      setError('Failed to update stage');
    } finally {
      setIsSaving(false);
    }
  };

  const performDelete = async () => {
    if (!stageToDelete) return;
    const stage = pipeline.stages?.find((s) => s.id === stageToDelete);
    if (stage && isSystemStage(stage)) {
      setError('This is a required default stage and cannot be deleted.');
      setStageToDelete(null);
      return;
    }

    try {
      setIsDeleting(true);
      await pipelineService.deleteStage(stageToDelete);
      const stages = pipeline.stages?.filter((s) => s.id !== stageToDelete) || [];
      onUpdate({ ...pipeline, stages });
      setStageToDelete(null);
    } catch (err) {
      console.error('Error deleting stage:', err);
      setError('Failed to delete stage. It may have associated tickets.');
    } finally {
      setIsDeleting(false);
    }
  };

  const startEdit = (stage: Stage) => {
    setEditingId(stage.id);
    setStageForm({
      name: stage.name,
      color: stage.color || '#3B82F6',
      description: stage.description || '',
      mappedStatus: stage.mappedStatus || 'in_progress',
      wipLimit: stage.wipLimit ?? null,
      requiredFields: stage.requiredFields || [],
      exitConditions: stage.exitConditions || {},
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setIsCreating(false);
    setStageForm(defaultStageForm);
  };

  // Resolve a field key to its display name (falls back to the raw key).
  const fieldLabel = (key: string) =>
    fieldDefinitions.find((f) => f.key === key)?.name || key;

  return (
    <>
    <div className="flex flex-col gap-[14px]">
      <SectionHead
        title="Workflow stages"
        description="The steps a ticket moves through in this pipeline, in order. Drag the handle to reorder — reordering changes the route for tickets from here on and never moves a ticket that is already in a stage."
        actions={
          <KitButton
            variant="primary"
            onClick={() => setIsCreating(true)}
            disabled={isCreating}
          >
            <Plus className="h-[15px] w-[15px]" aria-hidden="true" />
            Add stage
          </KitButton>
        }
      />

      {error && (
        <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <Notice tone="danger" headline="Something went wrong" body={error} />
          </div>
          <IconButton label="Dismiss error" onClick={() => setError(null)}>
            <X className="h-[15px] w-[15px]" aria-hidden="true" />
          </IconButton>
        </div>
      )}

      {/* Create Stage Form */}
      {isCreating && (
        <div
          className={`px-[18px] py-4 ${CARD_CLASS}`}
          style={{ ...CARD, borderColor: 'var(--co-brass-line)', background: 'var(--co-brass-wash)' }}
        >
          <h4 className="co-mono mb-4 text-[10px] font-semibold uppercase tracking-[.16em] text-[var(--co-brass-ink)]">
            New stage
          </h4>
          <StageForm
            form={stageForm}
            onChange={setStageForm}
            onSave={handleCreate}
            onCancel={cancelEdit}
            isSaving={isSaving}
            fieldDefinitions={fieldDefinitions}
          />
        </div>
      )}

      {/* Stages List */}
      <div className={`overflow-hidden ${CARD_CLASS}`} style={CARD}>
        {pipeline.stages?.length === 0 ? (
          <EmptyState
            title="No stages defined yet"
            body="A pipeline with no stages has nowhere to put a ticket. The five default stages are normally seeded with the pipeline."
            action={
              <KitButton variant="primary" onClick={() => setIsCreating(true)}>
                <Plus className="h-[15px] w-[15px]" aria-hidden="true" />
                Create your first stage
              </KitButton>
            }
          />
        ) : (
          <>
            <ColumnHeader
              columns={[
                { id: 'handle', label: '', width: 20 },
                { id: 'stage', label: 'Stage' },
                { id: 'status', label: 'Mapped status', width: 150 },
                { id: 'tickets', label: 'Tickets', width: 100, align: 'right' },
                { id: 'wip', label: 'WIP limit', width: 100, align: 'right' },
                { id: 'actions', label: '', width: 130 },
              ]}
            />

            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext
                items={(pipeline.stages || []).map((s) => s.id)}
                strategy={verticalListSortingStrategy}
              >
                <div>
                  {pipeline.stages?.map((stage, index) => {
                    const isFirst = index === 0;
                    const isLast = index === (pipeline.stages?.length || 0) - 1;
                    const isExpanded = expandedId === stage.id;
                    return (
                      <SortableStageItem key={stage.id} id={stage.id}>
                        {({ attributes, listeners, isDragging }) => (
                          <div
                            className="border-b border-[var(--co-line-subtle)] last:border-b-0"
                            style={
                              isDragging
                                ? {
                                    background: 'var(--co-raised)',
                                    boxShadow: 'var(--co-shadow-pop)',
                                  }
                                : undefined
                            }
                          >
                            {editingId === stage.id ? (
                              <div className="bg-[var(--co-selected)] px-[14px] py-4">
                                <StageForm
                                  form={stageForm}
                                  onChange={setStageForm}
                                  onSave={() => handleUpdate(stage.id)}
                                  onCancel={cancelEdit}
                                  isSaving={isSaving}
                                  fieldDefinitions={fieldDefinitions}
                                  lockIdentity={isSystemStage(stage)}
                                />
                              </div>
                            ) : (
                              <>
                                <div
                                  role="row"
                                  className="flex cursor-pointer items-center gap-3 border-l-2 px-[14px] py-[13px] transition-colors hover:bg-[var(--co-selected)]"
                                  style={{
                                    borderLeftColor: isExpanded ? 'var(--co-brass)' : 'transparent',
                                    background: isExpanded ? 'var(--co-selected)' : undefined,
                                  }}
                                  onClick={() => setExpandedId(isExpanded ? null : stage.id)}
                                >
                                  <div className="flex-none" style={{ width: 20 }}>
                                    <GripVertical
                                      {...attributes}
                                      {...listeners}
                                      onClick={(e) => e.stopPropagation()}
                                      aria-label="Drag to reorder stage"
                                      className="h-4 w-4 cursor-grab touch-none text-[var(--co-ink-4)] active:cursor-grabbing"
                                    />
                                  </div>

                                  <div className="flex min-w-0 flex-1 items-center gap-[10px]">
                                    <span
                                      className="h-[10px] w-[10px] flex-none rounded-full"
                                      style={{ backgroundColor: stage.color || 'var(--co-ink-4)' }}
                                      aria-hidden="true"
                                    />
                                    <span className="co-mono flex-none text-[10.5px] tabular-nums text-[var(--co-ink-4)]">
                                      {index + 1}
                                    </span>
                                    <div className="min-w-0">
                                      <div className="flex items-center gap-[8px]">
                                        <span className="truncate text-[13.5px] font-semibold text-[var(--co-ink)]">
                                          {stage.name}
                                        </span>
                                        {isFirst && <StatusChip tone="neutral">Entry</StatusChip>}
                                        {isLast && <StatusChip tone="neutral">Exit</StatusChip>}
                                      </div>
                                      {stage.description && (
                                        <p className="mt-[4px] truncate text-[12px] text-[var(--co-ink-3)]">
                                          {stage.description}
                                        </p>
                                      )}
                                    </div>
                                  </div>

                                  <div className="flex-none" style={{ width: 150 }}>
                                    {stage.mappedStatus ? (
                                      <StatusChip tone="neutral">
                                        {STATUS_OPTIONS.find((o) => o.value === stage.mappedStatus)?.label ||
                                          stage.mappedStatus}
                                      </StatusChip>
                                    ) : (
                                      <span className="text-[12.5px] text-[var(--co-ink-4)]">—</span>
                                    )}
                                  </div>

                                  {/* An em dash where the server sent no count.
                                      "No tickets here" and "nobody counted" are
                                      different answers, and only one of them is
                                      a reason to leave the stage alone. */}
                                  <div
                                    className="co-mono flex-none text-right text-[13px] font-semibold tabular-nums"
                                    style={{
                                      width: 100,
                                      color:
                                        stage.ticketCount === undefined
                                          ? 'var(--co-ink-4)'
                                          : 'var(--co-ink)',
                                    }}
                                  >
                                    {stage.ticketCount === undefined ? '—' : stage.ticketCount}
                                  </div>

                                  <div
                                    className="co-mono flex-none text-right text-[13px] tabular-nums"
                                    style={{
                                      width: 100,
                                      color: stage.wipLimit
                                        ? 'var(--co-terracotta-ink)'
                                        : 'var(--co-ink-4)',
                                      fontWeight: stage.wipLimit ? 600 : 400,
                                    }}
                                  >
                                    {stage.wipLimit || '—'}
                                  </div>

                                  <div
                                    className="flex flex-none items-center justify-end gap-2"
                                    style={{ width: 130 }}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <IconButton
                                      label={`Edit ${stage.name}`}
                                      onClick={() => startEdit(stage)}
                                    >
                                      <Edit2 className="h-[15px] w-[15px]" aria-hidden="true" />
                                    </IconButton>
                                    {!isSystemStage(stage) && (
                                      <IconButton
                                        label={`Delete ${stage.name}`}
                                        onClick={() => setStageToDelete(stage.id)}
                                        className="text-[var(--co-burgundy)] hover:border-[var(--co-burgundy)]"
                                      >
                                        <Trash2 className="h-[15px] w-[15px]" aria-hidden="true" />
                                      </IconButton>
                                    )}
                                    <span
                                      className="text-[var(--co-ink-4)]"
                                      aria-hidden="true"
                                      onClick={() => setExpandedId(isExpanded ? null : stage.id)}
                                    >
                                      {isExpanded ? (
                                        <ChevronUp className="h-4 w-4" />
                                      ) : (
                                        <ChevronDown className="h-4 w-4" />
                                      )}
                                    </span>
                                  </div>
                                </div>

                                {/* Expanded Details */}
                                {isExpanded && (
                                  <div className="border-t border-[var(--co-line-subtle)] bg-[var(--co-sunken)] px-[14px] py-4">
                                    <div className="grid grid-cols-2 gap-6">
                                      <div>
                                        <FieldLabel>Required fields</FieldLabel>
                                        <div className="mt-[7px]">
                                          {stage.requiredFields?.length ? (
                                            <div className="flex flex-wrap gap-[6px]">
                                              {stage.requiredFields.map((field) => (
                                                <StatusChip key={field} tone="neutral">
                                                  {fieldLabel(field)}
                                                </StatusChip>
                                              ))}
                                            </div>
                                          ) : (
                                            <span className="text-[12.5px] text-[var(--co-ink-4)]">
                                              None
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                      <div>
                                        <FieldLabel>Exit conditions</FieldLabel>
                                        <div className="mt-[7px]">
                                          {stage.exitConditions &&
                                          Object.keys(stage.exitConditions).length > 0 ? (
                                            <ul className="list-inside list-disc text-[12px] leading-[1.6] text-[var(--co-ink-2)]">
                                              {stage.exitConditions.requiresAssignee && (
                                                <li>Requires assignee</li>
                                              )}
                                              {stage.exitConditions.requiresResolution && (
                                                <li>Requires resolution</li>
                                              )}
                                              {stage.exitConditions.requiredFields?.length && (
                                                <li>
                                                  Required fields:{' '}
                                                  {stage.exitConditions.requiredFields.join(', ')}
                                                </li>
                                              )}
                                            </ul>
                                          ) : (
                                            <span className="text-[12.5px] text-[var(--co-ink-4)]">
                                              None
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        )}
                      </SortableStageItem>
                    );
                  })}
                </div>
              </SortableContext>
            </DndContext>

            <CardFoot
              left={`${pipeline.stages?.length ?? 0} stage${
                (pipeline.stages?.length ?? 0) === 1 ? '' : 's'
              }`}
              note="The five seeded stages cannot be deleted — a ticket whose stage vanished would have no status to map to."
            />
          </>
        )}
      </div>
    </div>

    <ConfirmDialog
      isOpen={stageToDelete !== null}
      loading={isDeleting}
      onClose={() => setStageToDelete(null)}
      onConfirm={performDelete}
      title="Delete stage"
      message="Are you sure you want to delete this stage?"
      confirmLabel="Delete"
      cancelLabel="Cancel"
      variant="danger"
    />
    </>
  );
}

// Inline multi-select for picking required field keys. Rendered in normal flow
// (not absolutely positioned) so it isn't clipped by the stage card's
// overflow-hidden when editing.
interface MultiSelectFieldsProps {
  options: FieldOption[];
  selected: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  emptyText?: string;
}

function MultiSelectFields({
  options,
  selected,
  onChange,
  placeholder = 'Select required fields...',
  emptyText = 'No fields defined yet. Add them in Settings → Field Definitions.',
}: MultiSelectFieldsProps) {
  const [open, setOpen] = useState(false);

  const toggle = (key: string) => {
    onChange(selected.includes(key) ? selected.filter((k) => k !== key) : [...selected, key]);
  };

  const selectedNames = options.filter((o) => selected.includes(o.key)).map((o) => o.name);

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex h-[34px] w-full items-center justify-between gap-2 rounded-lg border border-[var(--co-line-strong)] bg-[var(--co-raised)] px-3 text-left text-[13px] text-[var(--co-ink)] outline-none transition-colors focus:border-[var(--co-brass)]"
      >
        <span className={`truncate ${selected.length ? '' : 'text-[var(--co-ink-4)]'}`}>
          {selected.length ? selectedNames.join(', ') : placeholder}
        </span>
        {open ? (
          <ChevronUp className="h-4 w-4 flex-shrink-0 text-[var(--co-ink-4)]" />
        ) : (
          <ChevronDown className="h-4 w-4 flex-shrink-0 text-[var(--co-ink-4)]" />
        )}
      </button>

      {open && (
        <div className="mt-[6px] max-h-48 overflow-auto rounded-lg border border-[var(--co-line)] bg-[var(--co-raised)]">
          {options.length === 0 ? (
            <p className="px-3 py-2 text-[12.5px] text-[var(--co-ink-3)]">
              {emptyText}
            </p>
          ) : (
            options.map((o) => (
              <label
                key={o.key}
                className="flex cursor-pointer items-center gap-2 px-3 py-2 hover:bg-[var(--co-selected)]"
              >
                <input
                  type="checkbox"
                  checked={selected.includes(o.key)}
                  onChange={() => toggle(o.key)}
                  className="h-4 w-4 rounded accent-[var(--co-brass-ink)]"
                />
                <span className="text-[12.5px] text-[var(--co-ink-2)]">{o.name}</span>
                <code className="co-mono ml-auto text-[10.5px] text-[var(--co-ink-4)]">{o.key}</code>
              </label>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// Stage Form Component
interface StageFormProps {
  form: StageFormData;
  onChange: (form: StageFormData) => void;
  onSave: () => void;
  onCancel: () => void;
  isSaving: boolean;
  fieldDefinitions: FieldOption[];
  // When true this is a required default stage — its name and status are locked
  // (other fields like color/description/WIP stay editable).
  lockIdentity?: boolean;
}

function StageForm({ form, onChange, onSave, onCancel, isSaving, fieldDefinitions, lockIdentity = false }: StageFormProps) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="co-mono mb-[7px] block text-[9px] font-semibold uppercase tracking-[.16em] text-[var(--co-ink-3)]">
            Stage Name *
          </label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => onChange({ ...form, name: e.target.value })}
            placeholder="e.g., New, In Progress, Done"
            disabled={lockIdentity}
            className="h-[34px] w-full rounded-lg border border-[var(--co-line-strong)] bg-[var(--co-raised)] px-3 text-[13px] text-[var(--co-ink)] placeholder-[var(--co-ink-4)] outline-none transition-colors focus:border-[var(--co-brass)] disabled:cursor-not-allowed disabled:bg-[var(--co-sunken)] disabled:text-[var(--co-ink-4)]"
          />
          {lockIdentity && (
            <p className="mt-[5px] text-[11.5px] leading-[1.45] text-[var(--co-ink-3)]">
              Required default stage — name can't be changed.
            </p>
          )}
        </div>
        <div>
          <label className="co-mono mb-[7px] block text-[9px] font-semibold uppercase tracking-[.16em] text-[var(--co-ink-3)]">
            Status
          </label>
          <ThemedSelect
            value={form.mappedStatus}
            onChange={(e) => onChange({ ...form, mappedStatus: e.target.value })}
            disabled={lockIdentity}
            className="h-[34px] w-full rounded-lg border border-[var(--co-line-strong)] bg-[var(--co-raised)] px-3 text-[13px] text-[var(--co-ink)] placeholder-[var(--co-ink-4)] outline-none transition-colors focus:border-[var(--co-brass)] disabled:cursor-not-allowed disabled:bg-[var(--co-sunken)] disabled:text-[var(--co-ink-4)]"
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </ThemedSelect>
          <p className="mt-[5px] text-[11.5px] leading-[1.45] text-[var(--co-ink-3)]">
            {lockIdentity
              ? "Required default stage — status can't be changed."
              : 'Ticket status auto-set when entering this stage'}
          </p>
        </div>
      </div>

      <div>
        <label className="co-mono mb-[7px] block text-[9px] font-semibold uppercase tracking-[.16em] text-[var(--co-ink-3)]">
          Color
        </label>
        <div className="flex gap-2">
          {STAGE_COLORS.map((color) => (
            <button
              key={color.value}
              type="button"
              onClick={() => onChange({ ...form, color: color.value })}
              className={`w-8 h-8 rounded-full border-2 transition-all ${
                form.color === color.value
                  ? 'scale-110 border-[var(--co-ink)]'
                  : 'border-transparent hover:scale-105'
              }`}
              style={{ backgroundColor: color.value }}
              title={color.name}
            />
          ))}
        </div>
      </div>

      {/* WIP Limit — off by default. The toggle turns it on/off; when on, the
          number input sets the cap. wipLimit === null means "no limit" (the
          board only enforces a limit when wipLimit is a positive number). */}
      <div>
        <div className="flex items-center justify-between">
          <div>
            <label className="co-mono block text-[9px] font-semibold uppercase tracking-[.16em] text-[var(--co-ink-3)]">
              WIP Limit <span className="font-normal text-[var(--co-ink-4)]">(optional)</span>
            </label>
            <p className="text-[11.5px] text-[var(--co-ink-3)]">
              Cap how many tickets can sit in this stage. Blocks manual drops onto a full column.
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={form.wipLimit != null}
              onChange={(e) =>
                onChange({ ...form, wipLimit: e.target.checked ? (form.wipLimit ?? 10) : null })
              }
              className="sr-only peer"
            />
            <div className="peer h-[19px] w-[34px] rounded-full bg-[var(--co-line-strong)] after:absolute after:left-[2px] after:top-[2px] after:h-[15px] after:w-[15px] after:rounded-full after:bg-[var(--co-raised)] after:transition-all after:content-[''] peer-checked:bg-[var(--co-green)] peer-checked:after:translate-x-[15px]"></div>
          </label>
        </div>
        {form.wipLimit != null && (
          <div className="mt-2 flex items-center gap-2">
            <input
              type="number"
              value={form.wipLimit}
              onChange={(e) => {
                const n = parseInt(e.target.value, 10);
                onChange({ ...form, wipLimit: Number.isFinite(n) && n > 0 ? n : 1 });
              }}
              min={1}
              className="h-[34px] w-24 rounded-lg border border-[var(--co-line-strong)] bg-[var(--co-raised)] px-3 text-[13px] tabular-nums text-[var(--co-ink)] outline-none transition-colors focus:border-[var(--co-brass)]"
            />
            <span className="text-[12.5px] text-[var(--co-ink-3)]">tickets max</span>
          </div>
        )}
      </div>

      <div>
        <label className="co-mono mb-[7px] block text-[9px] font-semibold uppercase tracking-[.16em] text-[var(--co-ink-3)]">
          Description
        </label>
        <textarea
          value={form.description}
          onChange={(e) => onChange({ ...form, description: e.target.value })}
          placeholder="Describe what happens in this stage..."
          rows={2}
          className="min-h-[56px] w-full rounded-lg border border-[var(--co-line-strong)] bg-[var(--co-raised)] px-3 py-[10px] text-[12.5px] leading-[1.5] text-[var(--co-ink-2)] placeholder-[var(--co-ink-4)] outline-none transition-colors focus:border-[var(--co-brass)]"
        />
      </div>

      <div>
        <label className="co-mono mb-[7px] block text-[9px] font-semibold uppercase tracking-[.16em] text-[var(--co-ink-3)]">
          Required Fields <span className="font-normal text-[var(--co-ink-4)]">(optional)</span>
        </label>
        <MultiSelectFields
          options={fieldDefinitions}
          selected={form.requiredFields}
          onChange={(rf) => onChange({ ...form, requiredFields: rf })}
        />
        <p className="mt-[5px] text-[11.5px] leading-[1.45] text-[var(--co-ink-3)]">
          A ticket must have these fields filled before it can enter this stage. Choices come from Settings → Field Definitions.
        </p>
      </div>

      <div>
        <label className="co-mono mb-[7px] block text-[9px] font-semibold uppercase tracking-[.16em] text-[var(--co-ink-3)]">
          Exit Conditions <span className="font-normal text-[var(--co-ink-4)]">(optional)</span>
        </label>
        <MultiSelectFields
          options={EXIT_CONDITION_OPTIONS}
          placeholder="Select exit conditions..."
          emptyText="No exit conditions available."
          selected={[
            ...(form.exitConditions.requiresAssignee ? ['requiresAssignee'] : []),
            ...(form.exitConditions.requiresResolution ? ['requiresResolution'] : []),
          ]}
          onChange={(sel) => {
            // Preserve any other exit-condition keys; only set the two booleans
            // to true when selected (omit when unselected to keep the object clean).
            const next: StageExitConditions = { ...form.exitConditions };
            delete next.requiresAssignee;
            delete next.requiresResolution;
            if (sel.includes('requiresAssignee')) next.requiresAssignee = true;
            if (sel.includes('requiresResolution')) next.requiresResolution = true;
            onChange({ ...form, exitConditions: next });
          }}
        />
        <p className="mt-[5px] text-[11.5px] leading-[1.45] text-[var(--co-ink-3)]">
          A ticket must meet these before it can move out of this stage.
        </p>
      </div>

      <div className="flex justify-end gap-2">
        <KitButton onClick={onCancel}>Cancel</KitButton>
        <KitButton
          variant="primary"
          onClick={onSave}
          disabled={isSaving || !form.name.trim()}
          disabledReason={!form.name.trim() ? 'Name required' : undefined}
        >
          {isSaving ? <Spinner size="sm" /> : <Check className="h-4 w-4" aria-hidden="true" />}
          Save
        </KitButton>
      </div>
    </div>
  );
}
